/**
 * Persistent Think zip-picker request (M5 / M6).
 *
 * `requestOpenThinkPicker()` is the single entry point. The Sheet dock only
 * mounts on the next render, so a one-shot window event is lost below 1280.
 * This store keeps the request until a visible dock consumes it.
 *
 * Browsers only allow `input.click()` inside the original user gesture.
 * Approach: if the hidden file input is already registered in that gesture,
 * open the native dialog. After a render (Sheet mount / expand), do **not**
 * call `input.click()` — focus `[data-testid="think-zip-picker"]` and show a
 * CTA instead. No setTimeout.
 */

export const THINK_PICKER_PENDING_KEY = "mx.think-picker";
export const THINK_ZIP_PICKER_CTA = "Choose a .zip to stage";
export const THINK_ZIP_PICKER_CTA_HINT = "Click Choose a .zip to open the file dialog.";

export type ThinkPickerEventDetail = { gesture: boolean };

export type ThinkPickerAction = "open-dialog" | "focus-cta" | "idle";

let pending = false;
const listeners = new Set<() => void>();

function readSessionPending(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(THINK_PICKER_PENDING_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSessionPending(on: boolean) {
  try {
    if (on) globalThis.sessionStorage?.setItem(THINK_PICKER_PENDING_KEY, "1");
    else globalThis.sessionStorage?.removeItem(THINK_PICKER_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

function emit() {
  for (const fn of listeners) fn();
}

export function isThinkPickerPending(): boolean {
  return pending || readSessionPending();
}

/** Mark a picker request. Called only from `requestOpenThinkPicker()`. Does not notify yet. */
export function markThinkPickerPending(): void {
  pending = true;
  writeSessionPending(true);
}

/** Notify subscribers after the window event so a live dock can handle the gesture first. */
export function notifyThinkPickerPending(): void {
  emit();
}

/**
 * Capture activation **at request time**, before the synthetic picker event.
 * `window.event` inside the listener is that synthetic event (isTrusted: false).
 */
export function captureThinkPickerGesture(): boolean {
  try {
    if (typeof navigator !== "undefined" && navigator.userActivation?.isActive) return true;
  } catch {
    /* ignore */
  }
  try {
    const ev = typeof window !== "undefined" ? window.event : undefined;
    if (ev?.isTrusted) return true;
  } catch {
    /* ignore */
  }
  return false;
}

export function thinkPickerGestureFromEvent(event: Event): boolean {
  const detail = (event as CustomEvent<ThinkPickerEventDetail>).detail;
  return detail?.gesture === true;
}

export function consumeThinkPickerPending(): boolean {
  const was = pending || readSessionPending();
  pending = false;
  writeSessionPending(false);
  if (was) emit();
  return was;
}

export function subscribeThinkPickerPending(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * sameGesture + fileInputReady → native dialog (still in the click).
 * Otherwise the dock just mounted or the input is not registered yet → CTA.
 */
export function decideThinkPickerAction(args: {
  pending: boolean;
  sameGesture: boolean;
  fileInputReady: boolean;
}): ThinkPickerAction {
  if (!args.pending) return "idle";
  if (args.sameGesture && args.fileInputReady) return "open-dialog";
  return "focus-cta";
}
